import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

export default function GalleryGrid({ images = [], items = null, categories = null, cols = 3 }) {
  // Normalise to a list of { src, category }
  const allItems = useMemo(() => {
    if (items && items.length) return items.filter((it) => it && it.src);
    return images.filter(Boolean).map((src) => ({ src, category: null }));
  }, [items, images]);

  const showTabs = Array.isArray(categories) && categories.length > 1;
  const [filter, setFilter] = useState(categories ? categories[0] : "All");

  const pics = useMemo(() => {
    if (!showTabs || filter === "All") return allItems;
    return allItems.filter((it) => it.category === filter);
  }, [allItems, filter, showTabs]);

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const show = (i) => { setIndex(i); setOpen(true); };
  const prev = useCallback(() => setIndex((i) => (i === 0 ? pics.length - 1 : i - 1)), [pics.length]);
  const next = useCallback(() => setIndex((i) => (i === pics.length - 1 ? 0 : i + 1)), [pics.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  if (allItems.length === 0) return null;
  const colClass = cols === 2 ? "sm:grid-cols-2" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3";

  return (
    <>
      {showTabs && (
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-5 py-2.5 rounded-lg font-heading font-semibold text-sm transition-colors ${filter === c ? "bg-homi-gold text-homi-black" : "bg-homi-light text-homi-black hover:bg-homi-gold/15"}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className={`grid grid-cols-2 ${colClass} gap-4`}>
        {pics.map((it, i) => (
          <button key={`${it.src}-${i}`} onClick={() => show(i)} className="group relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={it.src} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <span className="absolute inset-0 bg-homi-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white" />
            </span>
            {it.category && (
              <span className="absolute top-3 left-3 bg-homi-black/70 text-white text-[11px] font-heading font-bold uppercase px-2.5 py-1 rounded-full">{it.category}</span>
            )}
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none">
          <div className="relative">
            <img src={pics[index]?.src} alt={`View ${index + 1}`} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <button onClick={() => setOpen(false)} className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white text-homi-black flex items-center justify-center shadow-lg">
              <X className="w-5 h-5" />
            </button>
            {pics.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-homi-gold text-homi-black flex items-center justify-center shadow-lg transition-colors">
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-homi-gold text-homi-black flex items-center justify-center shadow-lg transition-colors">
                  <ChevronRight className="w-6 h-6" />
                </button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-homi-black/70 text-white text-sm font-heading font-semibold px-3 py-1 rounded-full">
                  {index + 1} / {pics.length}
                </span>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
